import type { AxiosError } from 'axios';

import Notification from 'components/partials/notification/notification.helper';

import {
  UNAUTHORIZED,
  NOT_FOUND,
  FORBIDDEN,
  ACCOUNT_REFUSAL_MESSAGES,
  ACCOUNT_SETUP_FAILED_MESSAGE,
} from 'constants/error-types';
import oktaAuth from 'utils/okta/use-okta';
import {
  clearOktaSessionInfo,
  closeOktaSession,
  getOktaBearerToken,
} from 'helpers/okta-session';

export interface ApiErrorResponseData {
  detail?: string;
  type?: string;
  [key: string]: unknown;
}

export interface ApiErrorWithMeta extends Error {
  status?: number;
  statusText?: string;
  data?: ApiErrorResponseData;
}

export const sessionExpiredHandler = (
  title = 'Session Expired',
  message = 'Your session has expired, please log in again.',
  type = 'info',
  navigateToLogin = true
): void => {
  clearOktaSessionInfo();
  Notification.flash(title, type, message);
  if (navigateToLogin) window.location.href = '/login';
};

type AccountRefusalType = keyof typeof ACCOUNT_REFUSAL_MESSAGES;

const ACCOUNT_REFUSAL_TYPES = Object.keys(
  ACCOUNT_REFUSAL_MESSAGES
) as AccountRefusalType[];

const getAccountRefusalType = (
  status: number | undefined,
  data: ApiErrorResponseData | undefined
): AccountRefusalType | undefined => {
  if (status !== FORBIDDEN || typeof data?.type !== 'string') return undefined;

  const problemType = data.type;

  return ACCOUNT_REFUSAL_TYPES.find((refusalType) =>
    problemType.endsWith(refusalType)
  );
};

export const isAccountRefusalError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) return false;

  const { status, data } = error as ApiErrorWithMeta;

  return getAccountRefusalType(status, data) !== undefined;
};

export const failAccountSetup = async (
  authClient: Parameters<typeof closeOktaSession>[0]
): Promise<void> => {
  await closeOktaSession(authClient);
  sessionExpiredHandler(
    'Error logging in',
    ACCOUNT_SETUP_FAILED_MESSAGE,
    'error'
  );
};

const PUBLIC_URL_PREFIXES = ['/account/'];

const isPublicEndpoint = (url: string | undefined): boolean =>
  PUBLIC_URL_PREFIXES.some((prefix) => url?.startsWith(prefix));

// Guards against concurrent 401s (e.g. several in-flight queries expiring at once) each
// calling okta.signOut() again while the first is still mid-navigation, which would race
// the logout redirect.
let signOutInProgress = false;
let refusalInProgress = false;

export const errorInterceptors = async (
  error:
    | AxiosError<ApiErrorResponseData>
    | Record<string, never> = {} as Record<string, never>
): Promise<never> => {
  const axiosError = error as AxiosError<ApiErrorResponseData>;
  const { status, statusText, data } = axiosError?.response ?? {};

  if (!getOktaBearerToken() && !isPublicEndpoint(axiosError?.config?.url)) {
    sessionExpiredHandler();

    const err: ApiErrorWithMeta = Object.assign(
      new Error('Unauthorized: No bearer token'),
      { status, statusText, data }
    );

    return Promise.reject(err);
  }

  const refusalType = getAccountRefusalType(status, data);

  if (status && status === UNAUTHORIZED) {
    if (!signOutInProgress) {
      signOutInProgress = true;

      let signOutFailed = false;

      try {
        await oktaAuth.signOut();
      } catch {
        signOutFailed = true;
      }

      sessionExpiredHandler(undefined, undefined, undefined, signOutFailed);
    }
  } else if (refusalType) {
    if (!refusalInProgress) {
      refusalInProgress = true;

      await closeOktaSession(oktaAuth);
      sessionExpiredHandler(
        'Error logging in',
        ACCOUNT_REFUSAL_MESSAGES[refusalType],
        'error'
      );
    }
  } else if (
    status &&
    data &&
    status === NOT_FOUND &&
    data.detail === 'Subscription not active'
  ) {
    sessionExpiredHandler(
      'Error logging in',
      `Your school's subscription is locked.`,
      'error'
    );
  } else if (axiosError.response) {
    console.error('API response error', axiosError.response);
  }

  const err: ApiErrorWithMeta = Object.assign(
    new Error(`API Error: ${statusText || 'Unknown error'}`),
    { status, statusText, data }
  );

  return Promise.reject(err);
};
